import React, { useState, useEffect, useRef } from 'react'
import BreadCrumbComp from '../../../components/breadCrumb'
import { Grid } from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { callApiAsync, getNationalizationTimeById, getCountries, getProviders, getTransportModes, createOrUpdateNationalizationTime, uploadNationalizationTimeFiles, deleteNationalizationTimeFile } from '../../../services/apiService'
import SimpleSelect from '../../../components/selects/simpleSelect'
import FormSelect from '../../../components/selects/formSelect'
import FormTextField from '../../../components/textField/formTextField'
import FormCalendar from '../../../components/calendar/formCalendar'
import LaddaButton from '../../../components/buttons/laddaButton'
import { icons } from '../../../constants'
import { YesNoOptionsSelect } from '../../../constants/selectData'
import { format } from 'date-fns'
import { parseDateAsLocal } from '../../../utils/dateUtils'
// Fechas: mismo patrón que biomedicalEquipmentBase — parseDateAsLocal al cargar, format(new Date(...), "yyyy-MM-dd") al enviar.

const validationSchema = yup.object().shape({
    origen: yup
        .mixed()
        .nullable()
        .required('El origen es requerido'),
    nro_orden: yup
        .string()
        .required('El número de orden es requerido')
        .max(100, 'El número de orden no puede exceder 100 caracteres'),
    proveedor: yup
        .mixed()
        .nullable()
        .required('El proveedor es requerido'),
    modalidad_transporte: yup
        .mixed()
        .nullable()
        .required('La modalidad de transporte es requerida'),
    fecha_salida_proveedor: yup
        .date()
        .nullable()
        .required('La fecha de salida del proveedor es requerida'),
    fecha_llegada_aduanas: yup
        .date()
        .nullable()
        .required('La fecha de llegada a aduana es requerida')
        .test('fecha-consistente', 'La fecha de llegada a aduana debe ser igual o posterior a la fecha de salida del proveedor', function (value) {
            const { fecha_salida_proveedor } = this.parent;
            if (!value || !fecha_salida_proveedor) return true;
            return new Date(value) >= new Date(fecha_salida_proveedor);
        }),
    fecha_salida_aduanas: yup
        .date()
        .nullable()
        .test('fecha-consistente', 'La fecha de salida de aduana no puede ser menor a la fecha de llegada a aduana (pueden ser el mismo día)', function (value) {
            const { fecha_llegada_aduanas } = this.parent;
            if (!value || !fecha_llegada_aduanas) return true;
            return new Date(value) >= new Date(fecha_llegada_aduanas);
        }),
    fecha_llegada_bodega: yup
        .date()
        .nullable(),
    tiempo_transito: yup
        .string()
        .nullable(),
    dias_en_aduanas: yup
        .string()
        .nullable(),
    descargue_directo: yup
        .mixed()
        .nullable(),
    observaciones: yup
        .string()
        .nullable()
        .max(1000, 'Las observaciones no pueden exceder 1000 caracteres')
})

const CreateImportNationalizationTime = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const importId = searchParams.get('id')
    const isEditMode = !!importId

    const [attachedFiles, setAttachedFiles] = useState([])
    const [existingFiles, setExistingFiles] = useState([])
    const [loading, setLoading] = useState(false)
    const [countryOptions, setCountryOptions] = useState([])
    const [providerOptions, setProviderOptions] = useState([])
    const [transportModeOptions, setTransportModeOptions] = useState([])
    const [daysInCustomsColor, setDaysInCustomsColor] = useState(null)

    const fileInputRef = useRef(null)
    const urlApi = process.env.REACT_APP_API_URL

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onSubmit',
        defaultValues: {
            origen: null,
            nro_orden: "",
            proveedor: null,
            modalidad_transporte: null,
            fecha_salida_proveedor: null,
            fecha_llegada_aduanas: null,
            fecha_salida_aduanas: null,
            fecha_llegada_bodega: null,
            tiempo_transito: "",
            dias_en_aduanas: "",
            descargue_directo: undefined,
            observaciones: ""
        }
    })

    const watchFechaSalidaProveedor = watch("fecha_salida_proveedor")
    const watchFechaLlegadaAduanas = watch("fecha_llegada_aduanas")
    const watchFechaSalidaAduanas = watch("fecha_salida_aduanas")
    const watchDiasEnAduanas = watch("dias_en_aduanas")

    // Calcular tiempo en tránsito automáticamente
    useEffect(() => {
        if (watchFechaSalidaProveedor && watchFechaLlegadaAduanas) {
            const fechaSalida = new Date(watchFechaSalidaProveedor)
            const fechaLlegada = new Date(watchFechaLlegadaAduanas)

            if (fechaLlegada >= fechaSalida) {
                const diffTime = fechaLlegada - fechaSalida
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                setValue('tiempo_transito', String(diffDays))
            } else {
                setValue('tiempo_transito', "")
            }
        } else {
            setValue('tiempo_transito', "")
        }
    }, [watchFechaSalidaProveedor, watchFechaLlegadaAduanas, setValue])

    // Calcular días en aduana automáticamente
    useEffect(() => {
        if (watchFechaLlegadaAduanas && watchFechaSalidaAduanas) {
            const fechaLlegada = new Date(watchFechaLlegadaAduanas)
            const fechaSalida = new Date(watchFechaSalidaAduanas)

            if (fechaSalida >= fechaLlegada) {
                const diffTime = fechaSalida - fechaLlegada
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                setValue('dias_en_aduanas', String(diffDays))

                // Semaforización
                if (diffDays < 5) {
                    setDaysInCustomsColor('green')
                } else if (diffDays >= 5 && diffDays <= 10) {
                    setDaysInCustomsColor('yellow')
                } else {
                    setDaysInCustomsColor('red')
                }
            } else {
                setValue('dias_en_aduanas', "")
                setDaysInCustomsColor(null)
            }
        } else {
            setValue('dias_en_aduanas', "")
            setDaysInCustomsColor(null)
        }
    }, [watchFechaLlegadaAduanas, watchFechaSalidaAduanas, setValue])

    // Cargar opciones desde el backend
    useEffect(() => {
        callApiAsync(
            () => getCountries(),
            (resp) => {
                const data = resp?.data ?? resp;
                const countries = Array.isArray(data) ? data : [];
                setCountryOptions([
                    { name: "Seleccionar", value: null },
                    ...countries.map(c => ({ name: c.name, value: c.id }))
                ]);
            },
            (error) => {
                console.error('Error loading countries:', error);
            }
        );

        callApiAsync(
            () => getProviders(),
            (resp) => {
                const data = resp?.data ?? resp;
                const providers = Array.isArray(data) ? data : [];
                setProviderOptions([
                    { name: "Seleccionar", value: null },
                    ...providers.map(p => ({ name: p.name, value: p.id }))
                ]);
            },
            (error) => {
                console.error('Error loading providers:', error);
            }
        );

        callApiAsync(
            () => getTransportModes(),
            (resp) => {
                const data = resp?.data ?? resp;
                const transportModes = Array.isArray(data) ? data : [];
                setTransportModeOptions([
                    { name: "Seleccionar", value: null },
                    ...transportModes.map(tm => ({ name: tm.name, value: tm.id }))
                ]);
            },
            (error) => {
                console.error('Error loading transport modes:', error);
            }
        );
    }, [])

    // Cargar datos cuando hay ID (modo edición)
    useEffect(() => {
        if (importId && countryOptions.length > 0 && providerOptions.length > 0 && transportModeOptions.length > 0) {
            setLoading(true)
            callApiAsync(
                () => getNationalizationTimeById(importId),
                (resp) => {
                    const data = resp?.data ?? resp;

                    // Mapear datos del backend al formulario (origen usa FormSelect: guarda value/id)
                    if (data.origin_country_id) {
                        setValue('origen', data.origin_country_id);
                    }
                    if (data.orderNumber) setValue('nro_orden', data.orderNumber);
                    if (data.provider_id && data.provider) {
                        setValue('proveedor', data.provider_id);
                    }
                    if (data.transport_mode_id && data.transportMode) {
                        setValue('modalidad_transporte', data.transport_mode_id);
                    }
                    if (data.departureDateProvider) {
                        setValue('fecha_salida_proveedor', parseDateAsLocal(String(data.departureDateProvider).slice(0, 10)));
                    }
                    if (data.arrivalDateCustoms) {
                        setValue('fecha_llegada_aduanas', parseDateAsLocal(String(data.arrivalDateCustoms).slice(0, 10)));
                    }
                    if (data.departureDateCustoms) {
                        setValue('fecha_salida_aduanas', parseDateAsLocal(String(data.departureDateCustoms).slice(0, 10)));
                    }
                    if (data.storeArrivalDate) {
                        setValue('fecha_llegada_bodega', parseDateAsLocal(String(data.storeArrivalDate).slice(0, 10)));
                    }
                    if (data.transitTime !== null && data.transitTime !== undefined) {
                        setValue('tiempo_transito', String(data.transitTime));
                    }
                    if (data.daysInCustoms !== null && data.daysInCustoms !== undefined) {
                        setValue('dias_en_aduanas', String(data.daysInCustoms));
                    }
                    if (data.downloadDirect !== null && data.downloadDirect !== undefined) {
                        setValue('descargue_directo', Boolean(data.downloadDirect));
                    } else {
                        setValue('descargue_directo', undefined);
                    }
                    if (data.observations) setValue('observaciones', data.observations);

                    // Cargar archivos existentes
                    if (data.archivos && Array.isArray(data.archivos)) {
                        setExistingFiles(data.archivos);
                    }

                    setLoading(false);
                },
                setLoading
            );
        }
    }, [importId, setValue, countryOptions, providerOptions, transportModeOptions])

    const handleFileChange = (event) => {
        const files = event.target?.files;
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            const validFiles = fileArray.filter(file => {
                const ext = file.name.toLowerCase().split('.').pop();
                const validExtensions = ['doc', 'docx', 'xls', 'xlsx', 'pdf'];
                return validExtensions.includes(ext);
            });

            if (validFiles.length !== fileArray.length) {
                alert('Algunos archivos no son válidos. Solo se permiten Word, Excel o PDF.');
            }

            setAttachedFiles(prev => [...prev, ...validFiles]);
        }
    }

    const removeFile = (index) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    }

    const handleDeleteExistingFile = async (fileId) => {
        if (window.confirm('¿Está seguro de que desea eliminar este archivo?')) {
            setLoading(true);
            await callApiAsync(
                () => deleteNationalizationTimeFile(fileId),
                () => {
                    setExistingFiles(prev => prev.filter(file => file.id !== fileId));
                    setLoading(false);
                },
                setLoading
            );
        }
    }

    const handleDownloadFile = (fileUrl, fileName) => {
        const cleanedPath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
        const downloadUrl = cleanedPath.startsWith('http')
            ? cleanedPath
            : `${urlApi}${cleanedPath}`;
        window.open(downloadUrl, '_blank');
    }

    const onSubmit = async (formData) => {
        setLoading(true);

        try {
            const dataToSave = {
                id: importId || null,
                originCountryId: formData.origen?.value || formData.origen,
                orderNumber: formData.nro_orden || "",
                providerId: formData.proveedor?.value || formData.proveedor,
                transportModeId: formData.modalidad_transporte?.value || formData.modalidad_transporte,
                departureDateProvider: formData.fecha_salida_proveedor ? format(new Date(formData.fecha_salida_proveedor), 'yyyy-MM-dd') : null,
                arrivalDateCustoms: formData.fecha_llegada_aduanas ? format(new Date(formData.fecha_llegada_aduanas), 'yyyy-MM-dd') : null,
                departureDateCustoms: formData.fecha_salida_aduanas ? format(new Date(formData.fecha_salida_aduanas), 'yyyy-MM-dd') : null,
                storeArrivalDate: formData.fecha_llegada_bodega ? format(new Date(formData.fecha_llegada_bodega), 'yyyy-MM-dd') : null,
                transitTime: formData.tiempo_transito ? Number(formData.tiempo_transito) : null,
                daysInCustoms: formData.dias_en_aduanas ? Number(formData.dias_en_aduanas) : null,
                downloadDirect: typeof formData.descargue_directo === 'boolean'
                    ? formData.descargue_directo
                    : (formData.descargue_directo?.value !== undefined ? Boolean(formData.descargue_directo.value) : false),
                observations: formData.observaciones || null
            }

            await callApiAsync(
                () => createOrUpdateNationalizationTime(dataToSave),
                async (resp) => {
                    const data = resp?.data ?? resp;
                    const savedId = data.id || importId;

                    // Subir archivos si hay
                    if (attachedFiles.length > 0 && savedId) {
                        await callApiAsync(
                            () => uploadNationalizationTimeFiles(savedId, attachedFiles),
                            () => {
                                navigate('/foreign_trade_board/nationalization_Time')
                            },
                            setLoading
                        );
                    } else {
                        navigate('/foreign_trade_board/nationalization_Time')
                    }
                },
                setLoading
            );
        } catch (error) {
            console.error('Error al guardar:', error);
            setLoading(false);
        }
    }

    const getColorCircle = () => {
        if (!daysInCustomsColor) return null;
        const colorMap = {
            green: '#4CAF50',
            yellow: '#FFC107',
            red: '#F44336'
        };
        return colorMap[daysInCustomsColor];
    }

    return (
        <div>
            <BreadCrumbComp />
            <div className="searchTitle">
                <h1>{isEditMode ? 'EDITAR IMPORTACIÓN' : 'CREAR IMPORTACIÓN NUEVA'}</h1>
            </div>

            <div className="table-container">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-20">
                        <h2 className="title-bio-details-chard" style={{ marginBottom: '20px' }}>
                            IMPORTACIÓN
                        </h2>

                        <Grid container spacing={3}>
                            {/* Columna Izquierda */}
                            <Grid item xs={12} md={4}>
                                <div className="mb-15">
                                    <FormSelect
                                        name="origen"
                                        control={control}
                                        label="Origen *"
                                        formLabel="Seleccionar País"
                                        options={countryOptions}
                                        margin="0"
                                        error={errors.origen}
                                    />
                                </div>

                                <div className="mb-15">
                                    <p className="select-form-label">Modalidad de Transporte *</p>
                                    <SimpleSelect
                                        name="modalidad_transporte"
                                        control={control}
                                        options={transportModeOptions}
                                        margin={"0"}
                                        error={errors.modalidad_transporte}
                                    />

                                </div>

                                <div className="mb-15">
                                    <p className="select-form-label">Tiempo en Tránsito (días)</p>
                                    <FormTextField
                                        name="tiempo_transito"
                                        control={control}
                                        disabled={true}
                                        margin={"0"}
                                    />
                                </div>

                                <div className="mb-15">
                                    <p className="select-form-label">Fecha llegada a bodega</p>
                                    <FormCalendar
                                        name="fecha_llegada_bodega"
                                        control={control}
                                        formLabel="Seleccionar Fecha"
                                        margin={"0"}
                                        minDate={watchFechaSalidaAduanas}
                                    />
                                </div>
                            </Grid>

                            {/* Columna Central */}
                            <Grid item xs={12} md={4}>
                                <div className="mb-15">
                                    <p className="select-form-label">Nro de Orden *</p>
                                    <FormTextField
                                        name="nro_orden"
                                        control={control}
                                        margin={"0"}
                                        error={errors.nro_orden}
                                    />

                                </div>

                                <div className="mb-15">
                                    <p className="select-form-label">Fecha salida Institución Proveedor *</p>
                                    <FormCalendar
                                        name="fecha_salida_proveedor"
                                        control={control}
                                        formLabel="Seleccionar Fecha"
                                        margin={"0"}
                                        error={errors.fecha_salida_proveedor}
                                    />

                                </div>

                                <div className="mb-15">
                                    <p className="select-form-label">Fecha salida Aduana</p>
                                    <FormCalendar
                                        name="fecha_salida_aduanas"
                                        control={control}
                                        formLabel="Seleccionar Fecha"
                                        margin={"0"}
                                        error={errors.fecha_salida_aduanas}
                                        minDate={watchFechaLlegadaAduanas}
                                    />

                                </div>

                                <div className="mb-15">
                                    <p className="select-form-label">Descargue Directo</p>
                                    <SimpleSelect
                                        name="descargue_directo"
                                        control={control}
                                        options={YesNoOptionsSelect}
                                        margin={"0"}
                                    />
                                </div>
                            </Grid>

                            {/* Columna Derecha */}
                            <Grid item xs={12} md={4}>
                                <div className="mb-15">
                                    <p className="select-form-label">Proveedor *</p>
                                    <SimpleSelect
                                        name="proveedor"
                                        control={control}
                                        options={providerOptions}
                                        margin={"0"}
                                        error={errors.proveedor}
                                    />

                                </div>

                                <div className="mb-15">
                                    <p className="select-form-label">Fecha llegada a Aduana *</p>
                                    <FormCalendar
                                        name="fecha_llegada_aduanas"
                                        control={control}
                                        formLabel="Seleccionar Fecha"
                                        margin={"0"}
                                        error={errors.fecha_llegada_aduanas}
                                        minDate={watchFechaSalidaProveedor}
                                    />

                                </div>

                                <div className="mb-15">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <p className="select-form-label" >Nro de días en Aduana</p>
                                        {daysInCustomsColor && (
                                            <div
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    backgroundColor: getColorCircle(),
                                                    border: '2px solid #fff',
                                                    boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                                                }}
                                                title={
                                                    daysInCustomsColor === 'green' ? 'Menos de 5 días' :
                                                        daysInCustomsColor === 'yellow' ? 'Entre 5 y 10 días' :
                                                            'Más de 10 días'
                                                }
                                            />
                                        )}
                                    </div>
                                    <FormTextField
                                        name="dias_en_aduanas"
                                        control={control}
                                        disabled={true}
                                        margin={"0"}
                                    />
                                </div>
                            </Grid>

                            {/* Observaciones - Ancho completo */}
                            <Grid item xs={12}>
                                <div className="mb-15 w-complete">
                                    <p className="select-form-label">Observaciones</p>
                                    <FormTextField
                                        name="observaciones"
                                        control={control}
                                        label=""
                                        rows={3}
                                        isTextArea={true}
                                        error={errors.observaciones}
                                        maxLength={1000}
                                        margin={"0"}
                                    />
                                </div>
                            </Grid>

                            {/* Anexos */}
                            <Grid item xs={12}>
                                <div className="mb-15">
                                    <p className="select-form-label">Anexos</p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept=".doc,.docx,.xls,.xlsx,.pdf"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                    />
                                    <LaddaButton
                                        type="button"
                                        variant="contained"
                                        backgroundColor="#6C5CE7"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <div className="IHContainer align-center">

                                            <p className="button_text">Seleccionar archivos (Word, Excel, PDF)</p>
                                        </div>
                                    </LaddaButton>

                                    {(existingFiles.length > 0 || attachedFiles.length > 0) && (
                                        <div style={{ marginTop: '15px' }}>
                                            {/* Archivos existentes */}
                                            {existingFiles.map((file) => {
                                                const fileName = file.url ? file.url.split('/').pop() : `Archivo ${file.id}`;
                                                return (
                                                    <div key={`existing-${file.id}`} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        marginBottom: '8px',
                                                        padding: '8px',
                                                        backgroundColor: '#e3f2fd',
                                                        borderRadius: '4px'
                                                    }}>
                                                        <span style={{ flex: 1, fontSize: '14px' }}>{fileName}</span>
                                                        <LaddaButton
                                                            type="button"
                                                            variant="contained"
                                                            backgroundColor="#2196F3"
                                                            onClick={() => handleDownloadFile(file.url, fileName)}
                                                            style={{ minWidth: 'auto', padding: '5px 10px', marginRight: '5px' }}
                                                        >
                                                            <p className="button_text" style={{ fontSize: '12px' }}>Descargar</p>
                                                        </LaddaButton>
                                                        <LaddaButton
                                                            type="button"
                                                            variant="contained"
                                                            backgroundColor="#F44336"
                                                            onClick={() => handleDeleteExistingFile(file.id)}
                                                            style={{ minWidth: 'auto', padding: '5px 10px' }}
                                                        >
                                                            <p className="button_text" style={{ fontSize: '12px' }}>Eliminar</p>
                                                        </LaddaButton>
                                                    </div>
                                                );
                                            })}
                                            {/* Archivos nuevos */}
                                            {attachedFiles.map((file, index) => (
                                                <div key={`new-${index}`} style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    marginBottom: '8px',
                                                    padding: '8px',
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '4px'
                                                }}>
                                                    <span style={{ flex: 1, fontSize: '14px' }}>{file.name}</span>
                                                    <LaddaButton
                                                        type="button"
                                                        variant="contained"
                                                        backgroundColor="#F44336"
                                                        onClick={() => removeFile(index)}
                                                        style={{ minWidth: 'auto', padding: '5px 10px' }}
                                                    >
                                                        <p className="button_text" style={{ fontSize: '12px' }}>Eliminar</p>
                                                    </LaddaButton>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Grid>
                        </Grid>
                    </div>

                    {/* Botón de guardar */}
                    <div className="HContainer j-center" style={{ marginTop: '30px' }}>
                        <LaddaButton
                            type="submit"
                            variant="contained"
                            backgroundColor="#6C5CE7"
                            height="50px"
                            borderRadius="15px"
                            width="300px"
                            disabled={loading}
                        >
                            <div className="IHContainer align-center">
                                <p className="button_text">{isEditMode ? 'Actualizar Registro de Importación' : 'Crear Registro de Importación'}</p>
                            </div>
                        </LaddaButton>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default CreateImportNationalizationTime
